import {
  Alert,
  AlertsListQuery,
  AlertPatch,
  AlertBulkPatchRequest,
  AlertBulkPatchResult,
  AlertCounts,
  AlertMessageResponse,
  AlertStepsResponse,
  AlertApiError
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

async function handleErrorResponse(response: Response): Promise<never> {
  let errorData: AlertApiError;

  try {
    errorData = await response.json();
  } catch {
    errorData = {
      error: `Request failed: ${response.status} ${response.statusText}`,
      status: response.status,
      timestamp: new Date().toISOString()
    };
  }

  throw new Error(errorData.error);
}

export async function listAlerts(
  query: AlertsListQuery,
  signal?: AbortSignal
): Promise<{ items: Alert[]; nextCursor?: string }> {
  const params = new URLSearchParams();

  if (query.status !== undefined) {
    params.append('status', query.status.toString());
  }
  if (query.createdFrom) {
    params.append('createdFrom', query.createdFrom);
  }
  if (query.createdTo) {
    params.append('createdTo', query.createdTo);
  }
  if (query.messageId) {
    params.append('messageId', query.messageId);
  }
  if (query.q) {
    params.append('q', query.q);
  }
  if (query.changedBy) {
    params.append('changedBy', query.changedBy);
  }
  if (query.active !== undefined) {
    params.append('active', query.active.toString());
  }
  if (query.pageSize) {
    params.append('pageSize', query.pageSize.toString());
  }
  if (query.cursor) {
    params.append('cursor', query.cursor);
  }

  const response = await fetch(`${API_BASE_URL}/alerts?${params.toString()}`, { signal });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}

export async function getAlert(id: string, signal?: AbortSignal): Promise<Alert> {
  const response = await fetch(`${API_BASE_URL}/alerts/${id}`, { signal });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  const data = await response.json();
  return data.item;
}

export async function patchAlert(
  id: string,
  patch: AlertPatch,
  signal?: AbortSignal
): Promise<Alert> {
  const response = await fetch(`${API_BASE_URL}/alerts/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(patch),
    signal
  });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}

export async function bulkPatchAlerts(
  request: AlertBulkPatchRequest,
  signal?: AbortSignal
): Promise<AlertBulkPatchResult> {
  const response = await fetch(`${API_BASE_URL}/alerts/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request),
    signal
  });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}

export async function getAlertCounts(signal?: AbortSignal): Promise<AlertCounts> {
  const response = await fetch(`${API_BASE_URL}/alerts/counts`, { signal });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}

export async function getAlertMessage(
  id: string,
  signal?: AbortSignal
): Promise<AlertMessageResponse> {
  const response = await fetch(`${API_BASE_URL}/alerts/${id}/message`, { signal });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}

export async function getAlertSteps(
  id: string,
  signal?: AbortSignal
): Promise<AlertStepsResponse> {
  const response = await fetch(`${API_BASE_URL}/alerts/${id}/steps`, { signal });

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return await response.json();
}
